import { Y_SQUASH, type Camera } from './projection';

/** One repeat is deliberately large enough that its deterministic noise does not read as a stamp. */
export const GROUND_GRAIN_TILE_SIZE = 128;

function channel(color: number, shift: number): number {
  return Math.max(0, Math.min(255, Math.round(color * (1 + shift))));
}

function shifted(base: number, shift: number): readonly [number, number, number] {
  return [
    channel((base >>> 16) & 0xff, shift),
    channel((base >>> 8) & 0xff, shift),
    channel(base & 0xff, shift),
  ];
}

/** A deterministic, fully opaque, same-hue grain tile. The ±5% luminance band is subtle enough
 * to preserve entity contrast while breaking up the formerly flat seasonal fill. */
export function groundGrainTile(base: number): Uint8ClampedArray {
  const pixels = new Uint8ClampedArray(GROUND_GRAIN_TILE_SIZE * GROUND_GRAIN_TILE_SIZE * 4);
  const shades = [shifted(base, -0.05), shifted(base, 0), shifted(base, 0.05)] as const;
  for (let y = 0; y < GROUND_GRAIN_TILE_SIZE; y += 1) {
    for (let x = 0; x < GROUND_GRAIN_TILE_SIZE; x += 1) {
      let hash = Math.imul(x ^ 0x9e37, 0x85ebca6b) ^ Math.imul(y ^ 0x7f4a, 0xc2b2ae35);
      hash = Math.imul(hash ^ (hash >>> 16), 0x27d4eb2d);
      const shade = shades[(hash >>> 0) % shades.length]!;
      const offset = (y * GROUND_GRAIN_TILE_SIZE + x) * 4;
      pixels[offset] = shade[0];
      pixels[offset + 1] = shade[1];
      pixels[offset + 2] = shade[2];
      pixels[offset + 3] = 255;
    }
  }
  return pixels;
}

/** Pixi tiling transform that gives the screen-filling quad a world-space texture phase. */
export function groundGrainTransform(camera: Camera): {
  tilePositionX: number;
  tilePositionY: number;
  tileScaleX: number;
  tileScaleY: number;
} {
  return {
    tilePositionX: -camera.offsetX * camera.scale,
    tilePositionY: -camera.offsetY * camera.scale * Y_SQUASH,
    tileScaleX: camera.scale,
    tileScaleY: camera.scale * Y_SQUASH,
  };
}
